import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import admissionsHero1 from './assets/news/news1.jpg';
import admissionsHero2 from './assets/news/news5.jpg';
import admissionsHero3 from './assets/news/news8.jpg';
import admissionsDocImage from './assets/news/news4.jpg';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://permataharapansecondary.sch.id/api';

function AdmissionsPage({ page }) {
  const [cmsData, setCmsData] = useState(null);
  const [mediaMap, setMediaMap] = useState({});
  const [dataLoaded, setDataLoaded] = useState(false);
  const [mediaLoaded, setMediaLoaded] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);

  // Fetch media map
  const fetchMediaMap = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/admissions/cms/media?_t=${Date.now()}`, {
        credentials: 'include',
        headers: { 'Cache-Control': 'no-cache' }
      });
      if (!response.ok) {
        setMediaLoaded(true);
        return;
      }
      const result = await response.json();
      if (result.ok && Array.isArray(result.data)) {
        const map = {};
        result.data.forEach(m => {
          const id = m.id || m.media_id;
          const url = m.url || m.file_path;
          if (id && url) map[id] = map[String(id)] = url;
        });
        setMediaMap(map);
      }
    } catch (err) {
      console.error('[AdmissionsPage] Media fetch error:', err.message);
    } finally {
      setMediaLoaded(true);
    }
  }, []);

  // Fetch CMS sections
  const fetchCMSSections = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/admissions/cms/sections?published=true&_t=${Date.now()}`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'Cache-Control': 'no-cache' }
      });
      if (!response.ok) {
        setDataLoaded(true);
        return;
      }
      const result = await response.json();
      if (result.ok && Array.isArray(result.data)) {
        setCmsData(result.data);
      }
    } catch (err) {
      console.error('[AdmissionsPage] CMS fetch error:', err);
    } finally {
      setDataLoaded(true);
    }
  }, []);

  // Fetch on mount and every 30 seconds for live updates
  useEffect(() => {
    fetchMediaMap();
    fetchCMSSections();

    // Poll for updates every 30 seconds to refresh approved changes
    const intervalId = setInterval(() => {
      fetchMediaMap();
      fetchCMSSections();
    }, 30000);

    // Refresh when tab becomes visible (user returns to tab)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchMediaMap();
        fetchCMSSections();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchMediaMap, fetchCMSSections]);

  // Extract hero section from CMS
  const heroSection = useMemo(() => {
    if (!cmsData || cmsData.length === 0) return {};
    const section = cmsData.find(s => s.section_type === 'admissions-hero');
    if (!section) return {};
    
    try {
      let published = section.content_published;
      if (typeof published === 'string') published = JSON.parse(published);
      const enData = published?.en || {};
      return enData;
    } catch (e) {
      console.error('[AdmissionsPage] Failed to parse hero:', e);
      return {};
    }
  }, [cmsData]);

  // Extract levels section from CMS
  const levelsSection = useMemo(() => {
    if (!cmsData || cmsData.length === 0) return {};
    const section = cmsData.find(s => s.section_type === 'admissions-levels');
    if (!section) return {};
    
    try {
      let published = section.content_published;
      if (typeof published === 'string') published = JSON.parse(published);
      const enData = published?.en || {};
      return enData;
    } catch (e) {
      console.error('[AdmissionsPage] Failed to parse levels:', e);
      return {};
    }
  }, [cmsData]);

  // Resolve hero slides with media map
  const heroSlides = useMemo(() => {
    if (!heroSection?.slides || heroSection.slides.length === 0) {
      return [admissionsHero1, admissionsHero2, admissionsHero3];
    }
    return heroSection.slides.map(slide => {
      if (typeof slide === 'object' && slide.image) {
        return mediaMap[slide.image] || admissionsHero1;
      }
      return mediaMap[slide] || admissionsHero1;
    });
  }, [heroSection, mediaMap]);

  // Extract process section from CMS
  const processSection = useMemo(() => {
    if (!cmsData || cmsData.length === 0) return {};
    const section = cmsData.find(s => s.section_type === 'admissions-process');
    if (!section) return {};
    
    try {
      let published = section.content_published;
      if (typeof published === 'string') published = JSON.parse(published);
      const enData = published?.en || {};
      return enData;
    } catch (e) {
      console.error('[AdmissionsPage] Failed to parse process:', e);
      return {};
    }
  }, [cmsData]);

  // Extract requirements section from CMS
  const requirementsSection = useMemo(() => {
    if (!cmsData || cmsData.length === 0) return {};
    const section = cmsData.find(s => s.section_type === 'admissions-requirements');
    if (!section) return {};
    
    try {
      let published = section.content_published;
      if (typeof published === 'string') published = JSON.parse(published);
      const enData = published?.en || {};
      return enData;
    } catch (e) {
      console.error('[AdmissionsPage] Failed to parse requirements:', e);
      return {};
    }
  }, [cmsData]);

  // Extract key dates section from CMS
  const keyDatesSection = useMemo(() => {
    if (!cmsData || cmsData.length === 0) return {};
    const section = cmsData.find(s => s.section_type === 'admissions-key-dates');
    if (!section) return {};
    
    try {
      let published = section.content_published;
      if (typeof published === 'string') published = JSON.parse(published);
      const enData = published?.en || {};
      return enData;
    } catch (e) {
      console.error('[AdmissionsPage] Failed to parse keyDates:', e);
      return {};
    }
  }, [cmsData]);

  // Extract fees payment section from CMS
  const feesPaymentSection = useMemo(() => {
    if (!cmsData || cmsData.length === 0) return {};
    const section = cmsData.find(s => s.section_type === 'admissions-fees-payment');
    if (!section) return {};
    
    try {
      let published = section.content_published;
      if (typeof published === 'string') published = JSON.parse(published);
      const enData = published?.en || {};
      return enData;
    } catch (e) {
      console.error('[AdmissionsPage] Failed to parse feesPayment:', e);
      return {};
    }
  }, [cmsData]);

  // Extract online application section from CMS
  const onlineApplicationSection = useMemo(() => {
    if (!cmsData || cmsData.length === 0) return {};
    const section = cmsData.find(s => s.section_type === 'admissions-online-application');
    if (!section) return {};
    
    try {
      let published = section.content_published;
      if (typeof published === 'string') published = JSON.parse(published);
      const enData = published?.en || {};
      return enData;
    } catch (e) {
      console.error('[AdmissionsPage] Failed to parse onlineApplication:', e);
      return {};
    }
  }, [cmsData]);

  // Resolve process steps with media map
  const processSteps = useMemo(() => {
    if (processSection?.steps && processSection.steps.length > 0) {
      return processSection.steps.map(step => ({
        ...step,
        image: step.image ? mediaMap[step.image] : admissionsHero1
      }));
    }
    return [];
  }, [processSection, mediaMap]);

  // Resolve level images with media map
  const admissionsLevels = useMemo(() => {
    if (levelsSection?.levels && levelsSection.levels.length > 0) {
      return levelsSection.levels.map(level => ({
        ...level,
        image: level.image ? mediaMap[level.image] : admissionsHero1
      }));
    }
    return [];
  }, [levelsSection, mediaMap]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setActiveSlide((current) => (current + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(intervalId);
  }, [heroSlides.length]);

  useEffect(() => {
    const animatedItems = document.querySelectorAll('.adm-animate');
    if (!animatedItems.length) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );

    animatedItems.forEach((item) => observer.observe(item));

    return () => observer.disconnect();
  }, []);

  const heroTitle = heroSection?.title || 'Admissions';
  const heroSummary = heroSection?.summary || 'Begin your admissions journey with clear steps, supportive guidance, and a welcoming school community.';

  // Format key dates: convert database dateRange/date format to displayable format
  const formatKeyDatesForDisplay = (dates) => {
    if (!Array.isArray(dates)) return [];
    return dates.map(d => {
      let dateStart = '';
      let dateEnd = '';
      let displayDate = d.date || '';
      
      // Parse dateRange: "2026-01-15 to 2026-02-28"
      if (d.dateRange) {
        const parts = d.dateRange.split(' to ');
        dateStart = parts[0]?.trim() || '';
        dateEnd = parts[1]?.trim() || '';
        if (!displayDate && dateStart && dateEnd) {
          displayDate = `${new Date(dateStart).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${new Date(dateEnd).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
        }
      } else if (d.date) {
        dateStart = d.date;
        dateEnd = d.date;
        if (!displayDate) {
          displayDate = new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        }
      }
      
      return {
        title: d.title || '',
        date: displayDate,
        dateStart,
        dateEnd,
        description: d.description || '',
        tag: d.tag || ''
      };
    });
  };

  const keyDates = (keyDatesSection?.dates && keyDatesSection.dates.length > 0) 
    ? formatKeyDatesForDisplay(keyDatesSection.dates)
    : [];

  const feesPayment = feesPaymentSection?.blocks || [
    {
      title: 'Registration & Admission Fees',
      icon: 'fa-clipboard-list',
      summary: 'Clear onboarding fees so families know what to expect before enrollment.',
      description: 'One-time fees charged upon admission acceptance to secure your child\'s placement and complete enrollment.',
      items: [
        'Application and assessment fee (non-refundable)',
        'One-time registration fee upon acceptance',
        'Uniform and student kit (if applicable)',
        'Enrollment confirmation and placement security',
        'Access to student portal and parent dashboard',
      ],
      highlight: 'Transparent pricing with no hidden charges',
    },
    {
      title: 'Tuition & Payment Plans',
      icon: 'fa-credit-card',
      summary: 'Flexible payment options built around family needs and level pathways.',
      description: 'Annual tuition with multiple payment arrangements to support your family\'s financial planning.',
      items: [
        'Annual tuition based on level (Preschool, Primary, Secondary)',
        'Flexible installment options: monthly, per term, or semester',
        'Sibling discount or multi-child package (where applicable)',
        'Payment methods: bank transfer, virtual account, or on-site cashier',
        'Automatic payment reminders and online payment tracking',
        'Receipts and detailed billing via admissions portal',
      ],
      highlight: 'Installment plans available to ease financial burden',
    },
    {
      title: 'Scholarships & Financial Support',
      icon: 'fa-medal',
      summary: 'Limited assistance for qualifying students with transparent review steps.',
      description: 'Merit-based and needs-based scholarships available for eligible families. Limited seats with rolling review.',
      items: [
        'Merit-based scholarships for academic excellence',
        'Needs-based financial assistance (subject to availability)',
        'Transparent documentation and review process',
        'Application deadline and eligibility criteria provided',
        'Scholarship review conducted by admissions committee',
        'Contact admissions team for personalized guidance',
      ],
      highlight: 'Supporting talented students from all backgrounds',
    },
    {
      title: 'Additional Services & Programs',
      icon: 'fa-star',
      summary: 'Optional services that enhance your child\'s school experience.',
      description: 'Enrichment programs and support services available to complement your child\'s learning journey.',
      items: [
        'Transportation and shuttle service (optional, monthly fee)',
        'Lunch program with nutritious meal plans (optional)',
        'After-school care and enrichment clubs',
        'School trips, excursions, and special events',
        'Extended learning programs and tutoring support',
        'Technology and digital learning resources',
      ],
      highlight: 'Flexible add-ons to support your child\'s growth',
    },
  ];

  const onlineApplication = useMemo(() => {
    if (!onlineApplicationSection || Object.keys(onlineApplicationSection).length === 0) {
      return {
        title: '',
        summary: '',
        ctaLabel: '',
        note: '',
        image: admissionsDocImage,
        highlights: []
      };
    }
    return {
      title: onlineApplicationSection.title || '',
      summary: onlineApplicationSection.summary || '',
      ctaLabel: onlineApplicationSection.ctaLabel || '',
      note: onlineApplicationSection.note || '',
      image: (onlineApplicationSection.image && mediaMap[onlineApplicationSection.image]) || admissionsDocImage,
      highlights: Array.isArray(onlineApplicationSection.highlights) ? onlineApplicationSection.highlights : []
    };
  }, [onlineApplicationSection, mediaMap]);
  return (
    <section className="admissions-page-shell">
      <section className="admissions-hero">
        <div className="admissions-hero-media" aria-hidden="true">
          {heroSlides.map((slide, index) => (
            <div
              key={slide}
              className={`admissions-hero-slide ${index === activeSlide ? 'active' : ''}`}
            >
              <img src={slide} alt="Admissions hero" />
            </div>
          ))}
        </div>
        <div className="admissions-hero-overlay" aria-hidden="true" />
        <div className="admissions-hero-content">
          <p className="admissions-hero-kicker">Admissions</p>
          <h2>{heroTitle}</h2>
          <p>{heroSummary}</p>
          <div className="admissions-hero-dots" role="tablist" aria-label="Admissions hero slides">
            {heroSlides.map((slide, index) => (
              <button
                key={slide}
                type="button"
                className={`admissions-hero-dot ${index === activeSlide ? 'active' : ''}`}
                onClick={() => setActiveSlide(index)}
                aria-label={`Show slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="admissions-levels">
        <div className="admissions-levels-header">
          <p className="admissions-levels-kicker">{levelsSection?.subtitle || 'Admissions Pathways'}</p>
          <h3>{levelsSection?.title || 'Tailored Admissions for Every Stage'}</h3>
          <p>
            {levelsSection?.description || 'Discover admissions programs designed for Preschool through Secondary, each with clear processes, transparent timelines, and supportive guidance for families.'}
          </p>
        </div>

        <AdmissionsLevelsCarousel levels={admissionsLevels} />
      </section>

      <section className="admissions-process admissions-process-steps" id="admissions-process">
        <div className="admissions-process-header">
          <p className="admissions-process-kicker">{processSection?.subtitle || 'Admissions Process'}</p>
          <h3>{processSection?.title || 'Your child\'s journey starts here, with guidance at every step'}</h3>
          <p>
            {processSection?.description || 'Discover each milestone from inquiry to opportunity. Hover or swipe the cards to see what happens next and how we support your family along the way.'}
          </p>
        </div>

        <AdmissionsProcessSteps steps={processSteps} />
      </section>



      <section className="admissions-requirements" id="admissions-requirements">
        <div className="admissions-requirements-header">
          <p className="admissions-requirements-kicker">{requirementsSection?.kicker || 'Requirements'}</p>
          <h3>{requirementsSection?.title || 'Documents & Eligibility by Level'}</h3>
          <p>{requirementsSection?.description || 'Clear requirements for each admission level to help families prepare their applications with confidence.'}</p>
        </div>

        <AdmissionsRequirementsSection requirementsSection={requirementsSection} />
      </section>

      <AdmissionsKeyDatesTimeline keyDates={keyDates} keyDatesSection={keyDatesSection} />

      <section className="fees-payment" id="fees-payment">
        <div className="fees-payment-header">
          <p className="fees-payment-kicker">{feesPaymentSection?.subtitle || 'Fees & Payment Information'}</p>
          <h3>{feesPaymentSection?.title || 'Transparent Fees, Flexible Plans & Clear Payment Options'}</h3>
          <p>
            {feesPaymentSection?.description || 'A comprehensive overview of admission fees, tuition, and payment options for each level. We believe in transparent pricing with no hidden charges, and we offer flexible payment arrangements to support your family\'s financial planning.'}
          </p>
        </div>

        <div className="fees-payment-grid">
          {feesPayment.map((block) => (
            <article className="fees-payment-card adm-animate" key={block.title}>
              <div className="fees-payment-card-header">
                <div className="fees-payment-icon">
                  <i className={`fas ${block.icon}`} />
                </div>
                <h4>{block.title}</h4>
              </div>
              <p className="fees-payment-summary">{block.summary}</p>
              <p className="fees-payment-description">{block.description}</p>
              <ul className="fees-payment-list">
                {block.items.map((item) => (
                  <li key={item}>
                    <span className="list-icon">
                      <i className="fas fa-check" />
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="fees-payment-highlight">
                <span className="highlight-icon">
                  <i className="fas fa-lightbulb" />
                </span>
                <p>{block.highlight}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="online-application" id="online-application">
        <div className="online-application-header">
          <p className="online-application-kicker">Online Application</p>
          <h2>{onlineApplication.title}</h2>
          <p>{onlineApplication.summary}</p>
        </div>

        <div className="online-application-card adm-animate">
          <div className="online-application-content">
            <h3>Apply Faster, Track Every Step</h3>
            <p className="online-application-note">{onlineApplication.note}</p>
            <div className="online-application-highlights">
              {Array.isArray(onlineApplication.highlights) && onlineApplication.highlights.map((item) => (
                <span key={item}>{item}</span>
              ))}
            </div>
            <div className="online-application-actions">
              <a className="online-application-cta" href="/register">
                {onlineApplication.ctaLabel}
              </a>
            </div>
          </div>
          <div className="online-application-media" aria-hidden="true">
            <img src={onlineApplication.image} alt="Online application" />
            <div className="online-application-badge">
              <strong>Start Online</strong>
              <span>Submit, schedule, visit campus</span>
            </div>
          </div>
        </div>
      </section>
    </section>
  );
}


function AdmissionsLevelsCarousel({ levels }) {
  const trackRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const handlePrev = () => {
    const newIndex = activeIndex === 0 ? levels.length - 1 : activeIndex - 1;
    setActiveIndex(newIndex);
    scrollToCard(newIndex);
  };

  const handleNext = () => {
    const newIndex = activeIndex === levels.length - 1 ? 0 : activeIndex + 1;
    setActiveIndex(newIndex);
    scrollToCard(newIndex);
  };

  const scrollToCard = (index) => {
    const track = trackRef.current;
    if (!track) return;
    
    const card = track.children[index];
    if (card) {
      // Calculate scroll position to center the card
      const cardWidth = card.offsetWidth;
      const trackWidth = track.offsetWidth;
      const cardLeft = card.offsetLeft;
      
      // Center position = card position - (viewport width / 2) + (card width / 2)
      const scrollPosition = cardLeft - (trackWidth / 2) + (cardWidth / 2);
      
      track.scrollTo({
        left: scrollPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="admissions-levels-carousel-shell">
      <button
        className="admissions-levels-carousel-nav left"
        onClick={handlePrev}
        aria-label="Previous admissions level"
      >
        â†
      </button>

      <div className="admissions-levels-carousel-viewport">
        <div className="admissions-levels-carousel-track" ref={trackRef}>
          {levels.map((level, index) => (
            <article
              key={level.title}
              className={`admissions-level-card adm-animate ${index === activeIndex ? 'active' : ''}`}
              onClick={() => {
                setActiveIndex(index);
                scrollToCard(index);
              }}
            >
              <div className="admissions-level-header-section">
                <div className="admissions-level-top">
                  <h4>{level.title}</h4>
                  <span className="admissions-level-badge">{level.note}</span>
                </div>
                <p className="admissions-level-age">{level.ageRange}</p>
              </div>

              <div className="admissions-level-media">
                <img src={level.image} alt={level.title} />
              </div>

              <div className="admissions-level-body">
                <p className="admissions-level-description">{level.description}</p>

                <div className="admissions-level-focus">
                  <div className="admissions-level-focus-card">
                    <span>Assessment</span>
                    <p>{level.assessment}</p>
                  </div>
                  <div className="admissions-level-focus-card">
                    <span>Family Support</span>
                    <p>{level.support}</p>
                  </div>
                </div>
                
                <div className="admissions-level-highlights">
                  <h5>Key Checkpoints</h5>
                  <ul>
                    {level.highlights.map((highlight) => (
                      <li key={highlight}>{highlight}</li>
                    ))}
                  </ul>
                </div>

                <div className="admissions-level-pathway">
                  <h5>Pathway</h5>
                  <div className="admissions-level-steps">
                    {level.process.split('â†’').map((step) => (
                      <span key={`${level.title}-${step}`}>{step.trim()}</span>
                    ))}
                  </div>
                </div>

                <div className="admissions-level-outcomes">
                  <h5>Expected Outcomes</h5>
                  <ul>
                    {level.outcomes.map((outcome) => (
                      <li key={outcome}>{outcome}</li>
                    ))}
                  </ul>
                </div>

                <div className="admissions-level-timeline">
                  <div className="timeline-item">
                    <span className="timeline-label">Intake Window</span>
                    <p>{level.intake}</p>
                  </div>
                  <div className="timeline-item">
                    <span className="timeline-label">Estimated Timeline</span>
                    <p>{level.timeline}</p>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>

      <button
        className="admissions-levels-carousel-nav right"
        onClick={handleNext}
        aria-label="Next admissions level"
      >
        â†’
      </button>
    </div>
  );
}

function AdmissionsProcessSteps({ steps = [] }) {
  const defaultSteps = [
    {
      title: 'Inquiry',
      description: 'Contact our admissions team to get program details, fees, and available intake slots.',
      image: admissionsHero2,
    },
    {
      title: 'Visit',
      description: 'Schedule a campus tour or information session to meet the team and explore facilities.',
      image: admissionsHero1,
    },
    {
      title: 'Test/Interview',
      description: 'Student observation, assessment, or interview to understand readiness and fit.',
      image: admissionsHero3,
    },
    {
      title: 'Acceptance',
      description: 'Receive admission results and complete registration to secure your childâ€™s place.',
      image: admissionsHero2,
    },
    {
      title: 'Scholarship',
      description: 'Qualifying students may apply for scholarships or financial support. Limited seats, transparent review.',
      image: admissionsDocImage,
    },
    {
      title: 'Opportunity',
      description: 'Unlock student opportunities through programs, enrichment, and a supportive school community.',
      image: admissionsHero1,
    },
  ];
  
  const displaySteps = steps && steps.length > 0 ? steps : defaultSteps;
  const trackRef = useRef(null);
  const [activeStep, setActiveStep] = useState(0);

  const scrollToStep = (index) => {
    const clampedIndex = Math.max(0, Math.min(displaySteps.length - 1, index));
    setActiveStep(clampedIndex);
    const track = trackRef.current;
    const panel = track?.children?.[clampedIndex];
    if (panel && panel.scrollIntoView) {
      panel.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
    }
  };

  return (
    <div className="admissions-carousel-shell">
      <div className="admissions-carousel-track" ref={trackRef}>
        {displaySteps.map((step, index) => (
          <article
            key={step.title}
            className={`admissions-carousel-panel ${index === activeStep ? 'active' : ''}`}
            onMouseEnter={() => setActiveStep(index)}
            onFocus={() => setActiveStep(index)}
            onClick={() => scrollToStep(index)}
            tabIndex={0}
            role="button"
            aria-label={`Admissions step ${index + 1}: ${step.title}`}
          >
            <div
              className="admissions-carousel-image"
              style={{ backgroundImage: `url(${step.image})` }}
              aria-hidden="true"
            />
            <div className="admissions-carousel-overlay" aria-hidden="true" />
            <div className="admissions-carousel-content">
              <h3>{step.title}</h3>
              <p>{step.description}</p>
            </div>
          </article>
        ))}
      </div>
      <div className="admissions-carousel-nav">
        <button
          type="button"
          className={`admissions-carousel-arrow left ${activeStep === 0 ? 'is-disabled' : ''}`}
          onClick={() => scrollToStep(activeStep - 1)}
          aria-label="Previous step"
          disabled={activeStep === 0}
        >
          â†
        </button>
        <button
          type="button"
          className={`admissions-carousel-arrow right ${activeStep === displaySteps.length - 1 ? 'is-disabled' : ''}`}
          onClick={() => scrollToStep(activeStep + 1)}
          aria-label="Next step"
          disabled={activeStep === displaySteps.length - 1}
        >
          â†’
        </button>
      </div>
    </div>
  );
}

function AdmissionsRequirementsSection({ requirementsSection = {} }) {
  const [activeTab, setActiveTab] = useState(0);

  const defaultRequirementsByLevel = [
    {
      level: 'Preschool & Kindergarten',
      description: 'Document Required For Admission',
      documents: [
        'Completed Application Form',
        'Copy of Applicant\'s Birth Certificate',
        'Copy of Family Card',
        'Copy of Parent\'s Identification Card',
        'Photograph of Applicant, with red background, size 3 x 4 cm',
        'Signed Form of Parents Acknowledgement on School Regulation',
      ],
      eligibility: [
        'Minimum age requirement based on level',
        'Readiness observation and assessment',
        'Parent-child interaction session',
      ],
    },
    {
      level: 'Primary',
      description: 'Prospective students are required to complete the admission registration form and submit all necessary supporting documents for the preliminary eligibility screening process.',
      documents: [
        'Copy of birth certificate',
        'Copy of passport/ID + KK',
        'Copy of KITAS (for foreign nationals)',
        '2 recent passport photos (3Ã—4 cm)',
        'Report cards (Grades 4â€“6)',
        'Vaccination record',
        'Health form/reports (if applicable)',
      ],
      eligibility: [
        'Academic assessment in English, Math, and reasoning',
        'Teacher observation and learning style evaluation',
        'Student interview to understand interests and goals',
      ],
    },
    {
      level: 'Secondary',
      description: 'Academic review and student interview to ensure alignment with learning goals, leadership, and character development.',
      documents: [
        'Copy of birth certificate',
        'Copy of passport/ID + KK',
        'Copy of KITAS (for foreign nationals)',
        '2 recent passport photos (3Ã—4 cm)',
        'Report cards (Grades 7â€“9)',
        'Vaccination record',
        'Health form/reports (if applicable)',
        'Academic transcript and recommendations',
      ],
      eligibility: [
        'Comprehensive academic assessment and aptitude testing',
        'In-depth student interview on aspirations and values',
        'Review of academic transcript and recommendations',
        'Leadership and character fit evaluation',
      ],
    },
  ];

  // Use database requirements if available, otherwise use defaults
  const requirements = requirementsSection?.requirements;
  const requirementsByLevel = requirements && requirements.length > 0 ? requirements : defaultRequirementsByLevel;

  return (
    <div className="admissions-requirements-container">
      <div className="admissions-requirements-tabs">
        {requirementsByLevel.map((item, index) => (
          <button
            key={item.level}
            className={`admissions-requirements-tab ${index === activeTab ? 'active' : ''}`}
            onClick={() => setActiveTab(index)}
          >
            <span className="tab-label">{item.level}</span>
          </button>
        ))}
      </div>

      <div className="admissions-requirements-content adm-animate">
        <div className="requirements-card">
          <div className="requirements-header">
            <h4>{requirementsByLevel[activeTab].level}</h4>
            <p className="requirements-description">{requirementsByLevel[activeTab].description}</p>
          </div>

          <div className="requirements-grid">
            <div className="requirements-section">
              <h5>Required Documents</h5>
              <ul className="requirements-list">
                {requirementsByLevel[activeTab].documents.map((doc, idx) => (
                  <li key={idx} className="requirement-item">
                    <span className="requirement-icon">âœ“</span>
                    <span>{doc}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="requirements-section">
              <h5>Eligibility & Assessment</h5>
              <ul className="requirements-list">
                {requirementsByLevel[activeTab].eligibility.map((item, idx) => (
                  <li key={idx} className="requirement-item">
                    <span className="requirement-icon">â†’</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="requirements-note">
            <p>
              <strong>Need help?</strong> Contact our admissions team for clarification on any requirements or to schedule a consultation.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function AdmissionsKeyDatesTimeline({ keyDates, keyDatesSection }) {
  // Calculate status based on date ranges
  const getCardStatus = (dateStart, dateEnd) => {
    if (!dateStart || !dateEnd) return 'upcoming';
    
    const now = new Date();
    const start = new Date(dateStart);
    const end = new Date(dateEnd);
    
    // Set end time to end of day
    end.setHours(23, 59, 59, 999);
    
    if (now < start) {
      return 'upcoming'; // Belum dimulai
    } else if (now <= end) {
      return 'active'; // Sedang berlangsung
    } else {
      return 'completed'; // Sudah selesai
    }
  };

  return (
    <section className="admissions-key-dates" id="admissions-key-dates">
      <div className="container">
        <div className="key-dates-header">
          <span className="section-label">Timeline</span>
          <h2>Important Dates & Deadlines</h2>
          <p>{keyDatesSection?.description || 'Mark your calendar with key admission dates to stay on track throughout the enrollment process.'}</p>
        </div>

        <div className="key-dates-grid">
          {keyDates.map((item, index) => {
            const status = getCardStatus(item.dateStart, item.dateEnd);
            return (
              <div key={item.title} className={`key-date-card ${status}`}>
                <div className="card-header">
                  <div className="card-number">{index + 1}</div>
                  <div className="card-month">{item.dateStart ? new Date(item.dateStart).toLocaleDateString('en-US', { month: 'short' }) : 'N/A'}</div>
                </div>
                <div className="card-body">
                  <div className="card-status-badge">
                    {status === 'active' && 'Active Now'}
                    {status === 'completed' && 'Completed'}
                    {status === 'upcoming' && 'Upcoming'}
                  </div>
                  <h3>{item.title}</h3>
                  <p className="card-date">{item.date || (item.dateStart ? `${new Date(item.dateStart).toLocaleDateString()} - ${new Date(item.dateEnd).toLocaleDateString()}` : 'N/A')}</p>
                  <p className="card-description">{item.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default AdmissionsPage;


